import { t } from '@avalara/bat-apps-shared/i18n';
import { SCol, SRow } from '@avalara/skylab-sdk/react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import conf from '../../conf';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { readAviatorCrushLeadCookie } from '../../lib/aviator-crush-cookie';
import { submitAviatorCrushSignup } from '../../lib/side-effects/aviator-crush';
import { selectState } from '../../selectors';
import Loading from '../loading';
import PageNotFound from '../page-not-found';
import { PageWrapper } from '../styled/skylab/page';
import { getCrushPrompt } from './crush-prompt';
import { getExistingSessionRedirect } from './existing-session-redirect';
import { forceDarkTheme } from './force-dark-theme';
import { hideShellChrome } from './hide-shell-chrome';
import LeadForm from './lead-form';

// No top-level SContainer here - AvaApp's shell already wraps every route's <main> in one;
// adding a second nests them and breaks mobile layout.
const AviatorCrushLanding = () => {
  const dispatch = useAppDispatch();
  const { data, features } = useSelector(selectState);
  // Gated by showAviatorCrush (PF-8819) rather than the route itself, matching
  // ProductCatalog's shouldShowProductCatalog pattern - keeps /aviator-crush always registered
  // in app.tsx (so it doesn't fall through to the /:requestedOfferFlow catch-all) while still
  // controlling when the event page actually goes live, and letting it be killed quickly if needed.
  // showAviatorCrush is also the flag avaapp's /aviator-crush route alias checks server-side
  // (PF-8594) - consolidated onto one flag (PF-8819) so a single flip controls both surfaces.
  //
  // Wait for data.featuresLoaded before treating the flag as off, same reason ProductCatalog's
  // shouldRender does: configureFeatures dispatches its /api/features result and any
  // ?showAviatorCrush=true query-param override asynchronously, so features.SHOWAVIATORCRUSH
  // reads as falsy on first render regardless of the real value. Redirecting to PageNotFound on
  // that first render fires before the override is ever applied.
  const enabled = !!features.SHOWAVIATORCRUSH;

  const crushPrompt = getCrushPrompt();
  const existingSessionRedirectUrl = enabled ? getExistingSessionRedirect(crushPrompt) : null;

  // Computed fresh each render (not gated on `enabled`, unlike existingSessionRedirectUrl above) -
  // gating it here would freeze it at "absent" from the render where `enabled` was still falsy
  // (see the comment above) and it would never reflect the real cookie once the flag turns on.
  // Nothing client-side sets this cookie mid-session for this page (only LeadForm's own submit
  // does, and that's followed immediately by a navigation), so recomputing it isn't at risk of
  // flip-flopping the page back into auto-submit mode.
  const cookieState = readAviatorCrushLeadCookie();
  const [autoSubmitRedirectUrl, setAutoSubmitRedirectUrl] = useState<string | null>(null);
  const [autoSubmitFailed, setAutoSubmitFailed] = useState(false);

  const redirectUrl = existingSessionRedirectUrl ?? autoSubmitRedirectUrl;

  useEffect(() => {
    if (!enabled) return;
    const stopWatchingShellChrome = hideShellChrome();
    const restoreTheme = forceDarkTheme();
    return () => {
      stopWatchingShellChrome();
      restoreTheme();
    };
  }, [enabled]);

  useEffect(() => {
    if (redirectUrl) window.location.href = redirectUrl;
  }, [redirectUrl]);

  // PF-8766: a returning attendee's lead cookie skips the form entirely - go straight to
  // provisioning/magic-link/redirect, and skip the MILES POST since attribution was already
  // submitted on the original visit (see submitAviatorCrushSignup's isReturningAttendee flag).
  // Gated on `enabled`/`data.featuresLoaded` so this can't fire on a stale/prepopulated feature
  // value before the real flag is known (see the SHOWAVIATORCRUSH comment above) - otherwise a
  // signup request could go out even though the page is about to render PageNotFound.
  // biome-ignore lint/correctness/useExhaustiveDependencies: cookieState is recomputed fresh each render (see above) and intentionally left out below so this effect doesn't re-run on every unrelated render.
  useEffect(() => {
    if (
      !enabled ||
      !data.featuresLoaded ||
      existingSessionRedirectUrl ||
      cookieState.status !== 'complete' ||
      autoSubmitFailed
    ) {
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const result = await submitAviatorCrushSignup(dispatch, { ...cookieState.lead, isReturningAttendee: true });
        if (cancelled) return;

        if (result.ok) {
          setAutoSubmitRedirectUrl(result.value.redirectUrl);
        } else {
          setAutoSubmitFailed(true);
        }
      } catch {
        if (!cancelled) setAutoSubmitFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, data.featuresLoaded, existingSessionRedirectUrl, autoSubmitFailed]);

  if (!data.featuresLoaded) return null;
  if (!enabled) return <PageNotFound />;
  if (redirectUrl) return <Loading />;
  // Auto-submit in flight (or about to be retried) - keep the form hidden until it fails.
  if (cookieState.status === 'complete' && !autoSubmitFailed) return <Loading />;

  // A partial cookie always prefills; a complete cookie only reaches here once its auto-submit
  // has failed, and should prefill (so the attendee isn't asked to retype everything) with the
  // failure already visible rather than making them resubmit once just to see it.
  const initialValues = cookieState.status !== 'absent' ? cookieState.lead : undefined;
  const initialSubmitFailed = cookieState.status === 'complete' && autoSubmitFailed;

  return (
    <PageWrapper>
      <SRow className='pad-all-sm justify-content-center'>
        <SCol center='' span='sm-10 md-8 lg-6'>
          <img
            src={`${conf.ASSET_PATH}/assets/images/avalara-aviator-logo.png`}
            alt='Avalara'
            data-ref='aviator-crush.logo'
            className='pad-bottom-md'
            height={48}
          />
          <h1 className='pad-bottom-sm'>{t('client.aviatorCrush.heading')}</h1>
          <p className='pad-bottom-md'>{t('client.aviatorCrush.subheading')}</p>
          <LeadForm crushPrompt={crushPrompt} initialValues={initialValues} initialSubmitFailed={initialSubmitFailed} />
        </SCol>
      </SRow>
    </PageWrapper>
  );
};

export default AviatorCrushLanding;
